import type { Dictionary } from '../i18n/registry.js';

/** The server's limits for a voice sample (uploads/service.ts): said before the upload, not after. */
export const SAMPLE_MIN_MS = 6_000;
export const SAMPLE_MAX_MS = 30_000;

/**
 * How long a recording runs, read by the browser before anything is uploaded. A recording
 * made by `MediaRecorder` carries no duration in its header, so the element reports
 * Infinity until it is asked to seek past the end. `null` when the browser cannot tell:
 * the server measures it again anyway, and its answer stands.
 */
export function measureAudioMs(file: Blob): Promise<number | null> {
  if (typeof URL.createObjectURL !== 'function') return Promise.resolve(null);
  return new Promise((resolve) => {
    const audio = document.createElement('audio');
    const url = URL.createObjectURL(file);
    const finish = (ms: number | null): void => {
      URL.revokeObjectURL(url);
      resolve(ms);
    };
    const settle = (): void => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        finish(Math.round(audio.duration * 1_000));
      }
    };
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      if (Number.isFinite(audio.duration)) return settle();
      audio.ondurationchange = settle;
      audio.currentTime = Number.MAX_SAFE_INTEGER;
    };
    audio.onerror = () => finish(null);
    setTimeout(() => finish(null), 5_000);
    audio.src = url;
  });
}

/** What is wrong with a sample's length, in the parent's words; `null` when nothing is. */
export function sampleLengthProblem(ms: number | null, t: Dictionary): string | null {
  if (ms === null) return null;
  // Rounded away from the limit, so 5.6 s never reads "6 s, at least 6 seconds".
  if (ms < SAMPLE_MIN_MS) return t.narration.sampleTooShort(Math.floor(ms / 1_000));
  if (ms > SAMPLE_MAX_MS) return t.narration.sampleTooLong(Math.ceil(ms / 1_000));
  return null;
}
